
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormData, ProtectedCharacteristic, KeyEvent, Person, Witness } from '@/hooks/useFormState';
import { ArrowLeft, Loader2, FileText, ChevronDown, ChevronUp, Calendar, Users, User } from 'lucide-react';

interface ReviewProps {
  formData: FormData;
  onPrev: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const Review: React.FC<ReviewProps> = ({ formData, onPrev, onSubmit, isSubmitting }) => {
  const [expandedFile, setExpandedFile] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  
  // Get human-readable names for protected characteristics
  const getCharacteristicLabel = (characteristic: ProtectedCharacteristic): string => {
    const labels: Record<ProtectedCharacteristic, string> = {
      'age': 'Age',
      'disability': 'Disability',
      'genderReassignment': 'Gender Reassignment',
      'marriageAndCivilPartnership': 'Marriage & Civil Partnership',
      'pregnancyAndMaternity': 'Pregnancy & Maternity',
      'race': 'Race',
      'religionOrBelief': 'Religion or Belief',
      'sex': 'Sex',
      'sexualOrientation': 'Sexual Orientation'
    };
    
    return labels[characteristic] || characteristic;
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Toggle file content expansion
  const toggleFileExpansion = (fileName: string) => {
    if (expandedFile === fileName) {
      setExpandedFile(null);
    } else {
      setExpandedFile(fileName);
    }
  };
  
  // Toggle section expansion
  const toggleSectionExpansion = (sectionId: string) => {
    if (expandedSection === sectionId) {
      setExpandedSection(null);
    } else {
      setExpandedSection(sectionId);
    }
  };

  // Truncate text for preview
  const truncateText = (text: string, maxLength = 200) => {
    if (!text) return "No text could be extracted";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  
  // Format date
  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Date not specified';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };
  
  // Helper to render events and people
  const renderEventsAndPeople = (data: { keyEvents: KeyEvent[], peopleInvolved: Person[], witnesses: Witness[] }) => {
    const hasEvents = data.keyEvents.length > 0;
    const hasPeople = data.peopleInvolved.length > 0;
    const hasWitnesses = data.witnesses.length > 0;
    
    if (!hasEvents && !hasPeople && !hasWitnesses) {
      return <p className="text-muted-foreground text-sm">No events or people added.</p>;
    }
    
    return (
      <div className="space-y-4">
        {/* Key Events */}
        {hasEvents && (
          <div>
            <h4 className="text-sm font-medium flex items-center mb-2">
              <Calendar className="h-4 w-4 mr-1 text-primary" />
              Key Events
            </h4>
            <ul className="space-y-2">
              {data.keyEvents.map((event) => (
                <li key={event.id} className="bg-slate-50 p-2 rounded-md text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">{formatDate(event.date)}</span>
                  </div>
                  <p className="mt-1 text-gray-700">{event.description}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* People Involved */}
        {hasPeople && (
          <div>
            <h4 className="text-sm font-medium flex items-center mb-2">
              <User className="h-4 w-4 mr-1 text-primary" />
              People Involved
            </h4>
            <ul className="space-y-2">
              {data.peopleInvolved.map((person) => (
                <li key={person.id} className="bg-slate-50 p-2 rounded-md text-sm">
                  <div className="flex flex-col sm:flex-row sm:justify-between">
                    <span className="font-medium">{person.name}</span>
                    <span className="text-slate-500">{person.role}</span>
                  </div>
                  <p className="mt-1 text-gray-700">{person.involvement}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Witnesses */}
        {hasWitnesses && (
          <div>
            <h4 className="text-sm font-medium flex items-center mb-2">
              <Users className="h-4 w-4 mr-1 text-primary" />
              Witnesses
            </h4>
            <ul className="space-y-2">
              {data.witnesses.map((witness) => (
                <li key={witness.id} className="bg-slate-50 p-2 rounded-md text-sm">
                  <div className="flex flex-col sm:flex-row sm:justify-between">
                    <span className="font-medium">{witness.name}</span>
                    <span className="text-slate-500">{witness.role}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2 text-center">
        <h2 className="text-2xl font-semibold tracking-tight">Review Your Information</h2>
        <p className="text-muted-foreground">Please review the information you've provided before submitting your form.</p>
      </div>

      <div className="space-y-8">
        {/* Personal Information Section */}
        <div className="bg-white p-5 rounded-lg border shadow-sm">
          <h3 className="text-lg font-medium text-secondary border-b pb-2 mb-3">Personal Information</h3>
          <dl className="space-y-2">
            <div className="grid grid-cols-3 gap-1">
              <dt className="font-medium text-muted-foreground">Name:</dt>
              <dd className="col-span-2">{formData.name}</dd>
            </div>
            <div className="grid grid-cols-3 gap-1">
              <dt className="font-medium text-muted-foreground">Email:</dt>
              <dd className="col-span-2">{formData.email}</dd>
            </div>
            <div className="grid grid-cols-3 gap-1">
              <dt className="font-medium text-muted-foreground">Telephone:</dt>
              <dd className="col-span-2">{formData.telephone}</dd>
            </div>
          </dl>
        </div>

        {/* Protected Characteristics Section */}
        <div className="bg-white p-5 rounded-lg border shadow-sm">
          <h3 className="text-lg font-medium text-secondary border-b pb-2 mb-3">Protected Characteristics</h3>
          <ul className="list-disc ml-5 space-y-1">
            {formData.protectedCharacteristics.map((characteristic) => (
              <li key={characteristic} className="text-gray-800">{getCharacteristicLabel(characteristic)}</li>
            ))}
          </ul>
        </div>

        {/* Standard Discrimination Types Section */}
        {formData.discriminationTypes.length > 0 && (
          <div className="bg-white p-5 rounded-lg border shadow-sm">
            <h3 className="text-lg font-medium text-secondary border-b pb-2 mb-3">Standard Discrimination Types</h3>
            <div className="space-y-4">
              {formData.discriminationTypes.includes('direct') && (
                <div>
                  <div 
                    className="flex justify-between items-center cursor-pointer py-2"
                    onClick={() => toggleSectionExpansion('direct')}
                  >
                    <h4 className="font-medium text-primary">Direct Discrimination</h4>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      {expandedSection === 'direct' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  {expandedSection === 'direct' ? (
                    <div className="mt-2 space-y-3">
                      <div className="p-3 bg-slate-50 rounded-md whitespace-pre-line text-gray-800">
                        {formData.directDiscriminationDetails || "No details provided"}
                      </div>
                      
                      <div className="mt-3 border-t pt-3">
                        <h5 className="font-medium text-sm mb-2">Events & People:</h5>
                        {renderEventsAndPeople(formData.directDiscriminationEventsAndPeople)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Click to view details, events, and people
                    </div>
                  )}
                </div>
              )}
              
              {formData.discriminationTypes.includes('indirect') && (
                <div>
                  <div 
                    className="flex justify-between items-center cursor-pointer py-2"
                    onClick={() => toggleSectionExpansion('indirect')}
                  >
                    <h4 className="font-medium text-primary">Indirect Discrimination</h4>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      {expandedSection === 'indirect' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  {expandedSection === 'indirect' ? (
                    <div className="mt-2 space-y-3">
                      <div className="p-3 bg-slate-50 rounded-md whitespace-pre-line text-gray-800">
                        {formData.indirectDiscriminationDetails || "No details provided"}
                      </div>
                      
                      <div className="mt-3 border-t pt-3">
                        <h5 className="font-medium text-sm mb-2">Events & People:</h5>
                        {renderEventsAndPeople(formData.indirectDiscriminationEventsAndPeople)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Click to view details, events, and people
                    </div>
                  )}
                </div>
              )}
              
              {formData.discriminationTypes.includes('harassment') && (
                <div>
                  <div 
                    className="flex justify-between items-center cursor-pointer py-2"
                    onClick={() => toggleSectionExpansion('harassment')}
                  >
                    <h4 className="font-medium text-primary">Harassment</h4>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      {expandedSection === 'harassment' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  {expandedSection === 'harassment' ? (
                    <div className="mt-2 space-y-3">
                      <div className="p-3 bg-slate-50 rounded-md whitespace-pre-line text-gray-800">
                        {formData.harassmentDetails || "No details provided"}
                      </div>
                      
                      <div className="mt-3 border-t pt-3">
                        <h5 className="font-medium text-sm mb-2">Events & People:</h5>
                        {renderEventsAndPeople(formData.harassmentEventsAndPeople)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Click to view details, events, and people
                    </div>
                  )}
                </div>
              )}
              
              {formData.discriminationTypes.includes('victimisation') && (
                <div>
                  <div 
                    className="flex justify-between items-center cursor-pointer py-2"
                    onClick={() => toggleSectionExpansion('victimisation')}
                  >
                    <h4 className="font-medium text-primary">Victimisation</h4>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      {expandedSection === 'victimisation' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  {expandedSection === 'victimisation' ? (
                    <div className="mt-2 space-y-3">
                      <div className="p-3 bg-slate-50 rounded-md whitespace-pre-line text-gray-800">
                        {formData.victimisationDetails || "No details provided"}
                      </div>
                      
                      <div className="mt-3 border-t pt-3">
                        <h5 className="font-medium text-sm mb-2">Events & People:</h5>
                        {renderEventsAndPeople(formData.victimisationEventsAndPeople)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Click to view details, events, and people
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Disability Discrimination Types Section */}
        {formData.protectedCharacteristics.includes('disability') && formData.disabilityDiscriminationTypes.length > 0 && (
          <div className="bg-white p-5 rounded-lg border shadow-sm">
            <h3 className="text-lg font-medium text-secondary border-b pb-2 mb-3">Disability Discrimination Types</h3>
            <div className="space-y-4">
              {formData.disabilityDiscriminationTypes.includes('directDisability') && (
                <div>
                  <div 
                    className="flex justify-between items-center cursor-pointer py-2"
                    onClick={() => toggleSectionExpansion('directDisability')}
                  >
                    <h4 className="font-medium text-primary">Direct Disability Discrimination</h4>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      {expandedSection === 'directDisability' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  {expandedSection === 'directDisability' ? (
                    <div className="mt-2 space-y-3">
                      <div className="p-3 bg-slate-50 rounded-md whitespace-pre-line text-gray-800">
                        {formData.directDisabilityDiscriminationDetails || "No details provided"}
                      </div>
                      
                      <div className="mt-3 border-t pt-3">
                        <h5 className="font-medium text-sm mb-2">Events & People:</h5>
                        {renderEventsAndPeople(formData.directDisabilityEventsAndPeople)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Click to view details, events, and people
                    </div>
                  )}
                </div>
              )}

              {formData.disabilityDiscriminationTypes.includes('discriminationArisingFromDisability') && (
                <div>
                  <div 
                    className="flex justify-between items-center cursor-pointer py-2"
                    onClick={() => toggleSectionExpansion('discriminationArisingFromDisability')}
                  >
                    <h4 className="font-medium text-primary">Discrimination Arising from Disability</h4>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      {expandedSection === 'discriminationArisingFromDisability' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  {expandedSection === 'discriminationArisingFromDisability' ? (
                    <div className="mt-2 space-y-3">
                      <div className="p-3 bg-slate-50 rounded-md whitespace-pre-line text-gray-800">
                        {formData.discriminationArisingFromDisabilityDetails || "No details provided"}
                      </div>
                      
                      <div className="mt-3 border-t pt-3">
                        <h5 className="font-medium text-sm mb-2">Events & People:</h5>
                        {renderEventsAndPeople(formData.discriminationArisingFromDisabilityEventsAndPeople)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Click to view details, events, and people
                    </div>
                  )}
                </div>
              )}
              
              {formData.disabilityDiscriminationTypes.includes('failureToMakeReasonableAdjustments') && (
                <div>
                  <div 
                    className="flex justify-between items-center cursor-pointer py-2"
                    onClick={() => toggleSectionExpansion('failureToMakeReasonableAdjustments')}
                  >
                    <h4 className="font-medium text-primary">Failure to Make Reasonable Adjustments</h4>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      {expandedSection === 'failureToMakeReasonableAdjustments' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  {expandedSection === 'failureToMakeReasonableAdjustments' ? (
                    <div className="mt-2 space-y-3">
                      <div className="p-3 bg-slate-50 rounded-md whitespace-pre-line text-gray-800">
                        {formData.failureToMakeReasonableAdjustmentsDetails || "No details provided"}
                      </div>
                      
                      <div className="mt-3 border-t pt-3">
                        <h5 className="font-medium text-sm mb-2">Events & People:</h5>
                        {renderEventsAndPeople(formData.failureToMakeReasonableAdjustmentsEventsAndPeople)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Click to view details, events, and people
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Uploaded Documents Section */}
        {formData.uploadedFiles.length > 0 && (
          <div className="bg-white p-5 rounded-lg border shadow-sm">
            <h3 className="text-lg font-medium text-secondary border-b pb-2 mb-3">Uploaded Documents</h3>
            <ul className="space-y-3">
              {formData.uploadedFiles.map((file, index) => (
                <li key={index} className="p-2 bg-slate-50 rounded-md">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleFileExpansion(file.name)}
                  >
                    <div className="flex items-center space-x-2">
                      <FileText className="h-5 w-5 text-blue-500" />
                      <span className="font-medium">{file.name}</span>
                      <span className="text-sm text-muted-foreground">({formatFileSize(file.size)})</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      {expandedFile === file.name ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  
                  {expandedFile === file.name && file.extractedText && (
                    <div className="mt-2 p-3 bg-white border border-gray-200 rounded-md">
                      <h4 className="text-sm font-medium mb-1">Extracted Content:</h4>
                      <div className="text-sm whitespace-pre-line max-h-40 overflow-y-auto">
                        {truncateText(file.extractedText, 500)}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex space-x-4 mt-8">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onPrev}
          disabled={isSubmitting}
          className="flex-1"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button 
          type="button" 
          onClick={onSubmit}
          disabled={isSubmitting}
          className="flex-1 bg-primary hover:bg-primary/90"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit'
          )}
        </Button>
      </div>
    </div>
  );
};

export default Review;
